<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';
import AccountModulesAPI from 'dashboard/api/accountModules';
import { groupModulesByArea } from './moduleAreas';

const { t, te } = useI18n();
const store = useStore();

const modules = ref([]);

// Modulo fora do contrato aparece travado, em vez de deixar o cliente clicar
// e levar 403. Quem ja esta ligado segue podendo ser desligado: a trava e so
// do lado de ligar.
const podeLigar = moduleItem => moduleItem.contracted || moduleItem.enabled;
const isLoading = ref(true);
const savingKey = ref('');

const moduleLabel = key =>
  t(`GENERAL_SETTINGS.FORM.MODULES.ITEMS.${key}.LABEL`);
const moduleDescription = key =>
  t(`GENERAL_SETTINGS.FORM.MODULES.ITEMS.${key}.DESCRIPTION`);
// Nomes como 'Cabeçalho de conversa' não diziam onde o efeito aparece.
const moduleWhere = key => {
  const whereKey = `GENERAL_SETTINGS.FORM.MODULES.ITEMS.${key}.WHERE`;
  if (!te(whereKey)) return '';
  return t('GENERAL_SETTINGS.FORM.MODULES.WHERE_LABEL', {
    place: t(whereKey),
  });
};
const areaLabel = id =>
  t(`GENERAL_SETTINGS.FORM.MODULES.AREAS.${id.toUpperCase()}`);
const areas = computed(() => groupModulesByArea(modules.value));

const loadModules = async () => {
  isLoading.value = true;
  try {
    const { data } = await AccountModulesAPI.get();
    modules.value = data.modules || [];
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.MODULES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// A lista inteira trava enquanto um toggle grava: a resposta de um pedido
// substitui a lista toda, e dois pedidos em paralelo se atropelariam.
// O Switch já flipou `moduleItem.enabled` via v-model antes de emitir
// `change`, então lemos o valor desejado direto do item e desfazemos na
// mão se a API falhar (a tela nunca pode mostrar "ligado" sem ter gravado).
const toggleModule = async moduleItem => {
  const desired = moduleItem.enabled;
  savingKey.value = moduleItem.key;
  try {
    const { data } = await AccountModulesAPI.update(moduleItem.key, {
      enabled: desired,
    });
    modules.value = data.modules || modules.value;
    // Menu, Hub e rotas leem as features do store da conta, carregado so no
    // boot: sem recarregar, o modulo so aparece (ou some) depois do F5. O
    // aviso espera a recarga para poder dizer que ja aparece.
    await store.dispatch('accounts/get', { silent: true });
    useAlert(
      t(
        desired
          ? 'GENERAL_SETTINGS.FORM.MODULES.TOGGLE_ON'
          : 'GENERAL_SETTINGS.FORM.MODULES.TOGGLE_OFF',
        { name: moduleLabel(moduleItem.key) }
      )
    );
  } catch (error) {
    moduleItem.enabled = !desired;
    useAlert(t('GENERAL_SETTINGS.FORM.MODULES.TOGGLE_ERROR'));
  } finally {
    savingKey.value = '';
  }
};

onMounted(loadModules);
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.MODULES.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.MODULES.NOTE')"
    with-border
  >
    <woot-loading-state v-if="isLoading" />
    <div v-else class="flex flex-col gap-8" data-testid="account-modules-list">
      <section
        v-for="area in areas"
        :key="area.id"
        :aria-labelledby="`account-modules-area-${area.id}`"
        :data-testid="`account-modules-area-${area.id}`"
      >
        <h5
          :id="`account-modules-area-${area.id}`"
          class="mb-2 text-sm font-semibold text-n-slate-12"
        >
          {{ areaLabel(area.id) }}
        </h5>
        <ul class="flex flex-col gap-4">
          <li
            v-for="moduleItem in area.modules"
            :key="moduleItem.key"
            class="flex items-center justify-between gap-4 py-2"
            :data-testid="`account-module-${moduleItem.key}`"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ moduleLabel(moduleItem.key) }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ moduleDescription(moduleItem.key) }}
              </p>
              <p
                v-if="moduleWhere(moduleItem.key)"
                class="text-xs text-n-slate-10"
                :data-testid="`account-module-where-${moduleItem.key}`"
              >
                {{ moduleWhere(moduleItem.key) }}
              </p>
              <p
                v-if="!podeLigar(moduleItem)"
                class="text-xs text-n-amber-11"
                :data-testid="`account-module-locked-${moduleItem.key}`"
              >
                {{ $t('GENERAL_SETTINGS.FORM.MODULES.NOT_CONTRACTED') }}
              </p>
            </div>
            <Switch
              v-model="moduleItem.enabled"
              :disabled="Boolean(savingKey) || !podeLigar(moduleItem)"
              :data-testid="`account-module-toggle-${moduleItem.key}`"
              @change="() => toggleModule(moduleItem)"
            />
          </li>
        </ul>
      </section>
    </div>
  </SectionLayout>
</template>
